import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, LinearProgress, Typography } from '@mui/material';
import { DropzoneArea } from 'material-ui-dropzone';
import { read, utils } from 'xlsx';
import './Import.css';
import IssuesTab from './IssuesTab';
import FileSummary from './FileSummary';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { importTasksByExcel } from 'src/services/timeManagementService';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from 'src/store/RootReducer';
import { AppActions } from 'src/store/AppReducer';
import SnackBar from 'src/components/SnackBar/SnackBar';

const ImportModalForm = ({ toggle, getData, projectId }) => {
  const dispatch = useDispatch();
  const appUser = useSelector((state: RootStateType) => state.appReducer?.user);

  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState('');
  const [fileTemplateStatus, setFileTemplateStatus] = useState(false);
  const [tableValues, setTableValues] = useState<any>([]);
  const [headers] = useState([
    'Reference Id',
    'Activity Name',
    'Activity Type',
    'Start Date',
    'End Date'
  ]);

  const [requiredFields] = useState([
    'Activity Name',
    'Activity Type',
    'Start Date'
  ]);

  const [isDisabled, setIsDisabled] = useState(true);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [attachment, setAttachment] = useState<any>({});
  const [progress, setProgress] = useState(0);

  const [successRecords, setSuccessRecords] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [invalidValues, setInvalidValues] = useState([]);
  const [invalidRows, setInvalidRows] = useState(0);

  const [openIssues, setOpenIssues] = useState(false);

  const onImport = () => {
    if (attachment) {
      const payload = {
        file: attachment.file,
        projectId: projectId,
        createBy: appUser.EmployeeId,
      }

      importTasksByExcel(payload).then((res) => {
        const data = res.data;

        if(data?.statusCode === 403){
          dispatch(AppActions.hideLoader());
          setSnackBarMessage(data?.statusCode ? data?.message : 'Unable to import activities');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
        } else {
          setSnackBarMessage('You’ve uploaded the activities successfully.');
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          setTimeout(() => {
            toggle();
            getData();
          }, 500);
        }
      })
      .catch((error) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage(error?.request?.response ? error?.request?.response : 'Unable to import');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
      });
    }
  };

  const handleFileChange = (files) => {
    setTableValues([]);
    setFileTemplateStatus(false);
    setProgress(0);
    setIsDisabled(true);
    if (files[0]) {
      setAttachment({ file: files[0] });
      setFileName(files[0].name);
      const reader = new FileReader();
      if (reader.readAsBinaryString) {
        reader.onload = (e) => {
          const workbook = read(reader.result, { type: 'binary' });
          setProgress(10);
          setIsDisabled(true);
          const headerSheets = workbook.Sheets[workbook.SheetNames[0]];
          const excelHeaders: any = utils.sheet_to_json(headerSheets, {
            header: 1
          });

          const firstSheet = workbook.SheetNames[0];
          const excelRows = utils.sheet_to_json(workbook.Sheets[firstSheet]);

          if (
            !excelHeaders[0]?.every((value, index) => value === headers[index])
          ) {
            setFileTemplateStatus(false);
            setErrors('File template is invalid');
          } else if(excelRows.length === 0){
            setFileTemplateStatus(false);
            setErrors('The file your uploading is empty/ doesn’t have any valid records');
          } else {
            setTableValues(excelRows);
            setFileTemplateStatus(true);
          }
        };
        setProgress(20);
        setIsDisabled(true);
        reader.readAsBinaryString(files[0]);
      }
    }
  };

  useEffect(() => {
    if (fileTemplateStatus) {
      setErrors('');
    }
  }, [fileTemplateStatus]);

  useEffect(() => {
    setProgress(0);
    setIsDisabled(true);
  }, [fileName]);

  const toggleIssues = () => {
    setOpenIssues(!openIssues);
  };

  useEffect(() => {
    setSuccessRecords(tableValues.length - invalidRows);
    setTotalRecords(tableValues.length);
  }, [tableValues, invalidRows]);

  useEffect(() => {
    if (tableValues.length > 0 && fileTemplateStatus) {
      let emptyRowArr = [];
      //let invalidRow = 0;
      tableValues.forEach((tab) => {
        const rowNum = tab.__rowNum__;
        setProgress(40);
        setIsDisabled(true);
        const entries = Object.entries(tab);
        const emptyFields = requiredFields.filter((head) => {
          if (!entries.some((val) => val[0] === head)) return head;
        });

        const emptyFieldsRows = emptyFields.map((field) => {
          return {
            error: `${field} is Required`,
            row: rowNum
          };
        });

        if (emptyFieldsRows.length > 0) {
          emptyRowArr = [...emptyRowArr, ...emptyFieldsRows];
        }
        setTimeout(() => {
          setProgress(70);
        }, 500);
        setIsDisabled(true);
      });

      setTimeout(() => {
        setProgress(100);
      }, 1000);
      setIsDisabled(false);
      setInvalidValues([...emptyRowArr]);
      //setInvalidRows(invalidRow);
    }
  }, [tableValues, requiredFields, fileTemplateStatus]);

  return (
    <>
      <Grid sx={{ px: 2, mt: '0.3rem' }} container spacing={2} className='import-modal-form'>
        <Grid item xs={12} display="flex" alignItems="center" justifyContent='space-between'>
          <Typography
            style={{
              color: '#223354',
              fontWeight: '700',
              fontSize: '15px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
            sx={{ pb: 1 }}
            variant="h4"
          >
            Load from XLS
          </Typography>
          <Button
            id="btn-onSubmit"
            sx={{
              ml: 2,
              fontSize: '13px',
              fontWeight: 'normal',
              background: '#0069E4',
            }}
            startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
            href={process.env.PUBLIC_URL + '/templates/activity-template.xlsx'}
            variant="contained"
            type="submit"
          >
            Download Template
          </Button>
        </Grid>
        {!fileTemplateStatus && (
          <Grid display="flex" alignItems="center" item xs={12}>
            <Typography sx={{ color: 'red' }} variant="body2">
              Please make sure to use the given template
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <DropzoneArea
            acceptedFiles={['.xlsx', '.xls']}
            onChange={(e) => handleFileChange(e)}
            showPreviews={false}
            showPreviewsInDropzone={false}
            filesLimit={1}
            showAlerts={false}
          />
          {errors && (
            <Typography sx={{ color: 'red' }} variant="body2">
              {errors}
            </Typography>
          )}
        </Grid>
        {fileTemplateStatus && (
          <Grid item xs={12}>
            <img
              src={process.env.PUBLIC_URL + '/img/excelsheet.jpg'}
              alt=""
              style={{
                width: '48px',
                height: '48px'
              }}
            />
            <Box sx={{ width: '100%' }}>
              <Grid item display="flex" justifyContent="between">
                <p style={{ marginBottom: 0 }}>{fileName}</p>
                <p style={{ marginBottom: 0, marginLeft: 'auto' }}>
                  {progress}
                </p>
              </Grid>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          </Grid>
        )}
        {tableValues?.length > 0 && (
          <FileSummary
            tableValues={tableValues}
            successRecords={successRecords}
            totalRecords={totalRecords}
            toggleIssues={toggleIssues}
            invalidRows={invalidRows}
            errorsCount={totalRecords - successRecords || 0}
          />
        )}
        <Grid container spacing={2}>
          <Grid
            style={{
              marginTop: '2rem',
              marginBottom: '1rem'
            }}
            item
            sx={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}
            xs={12}
          >
            <Button
              id="btn-onClose"
              sx={{
                width: '120px',
                fontWeight: 'normal',
                background: '#F5F7FF',
                fontSize: '13px'
              }}
              onClick={() => toggle()}
            >
              Cancel
            </Button>
            <Button
              id="btn-onSubmit"
              sx={{
                ml: 2,
                width: '120px',
                fontSize: '13px',
                fontWeight: 'normal',
                backgroundColor: isDisabled ? '#eee' : '#0069E4',
                color: isDisabled ? 'rgba(34, 51, 84, 0.5)' : '#FFFFFF',
              }}
              disabled={isDisabled}
              variant="contained"
              onClick={() => onImport()}
            >
              Import
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <SnackBar
        snackBarOpen={snackBarOpen}
        setSnackBarOpen={setSnackBarOpen}
        snackBarMessage={snackBarMessage}
        snackbarSeverity={snackbarSeverity}
      />
      <IssuesTab
        toggle={toggleIssues}
        open={openIssues}
        invalidValues={invalidValues}
      />
    </>
  );
};

export default ImportModalForm;
